import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { getSocket } from '@/hooks/useRealtimeSync';
import { useOverlayAnimation } from '@/hooks/useOverlayAnimation';
import { usePopoverPositioning } from '@/hooks/usePopoverPositioning';
import { CloseIcon } from '@/components/ui/Icons';
import type { MessengerSocketThreadMeta } from '@/lib/messengerNotificationPreview';
import { unwrapMessengerMessageSocketPayload, unwrapMessengerMessagesBatchSocketPayload } from '@/lib/messengerSocketPayload';
import { shouldSuppressMessengerMessageNotification } from '@/notifications/notificationOrchestrator';
import {
  buildOverlayItemsFromLoads,
  fetchInitialUnreadThreadLoads,
  type UnreadOverlayItem,
  type UnreadOverlayThreadLoad,
} from '@/components/Messenger/overlay/fetchUnreadMessagesForOverlay';
import {
  formatUnreadOverlayMessage,
  formatUnreadOverlayMessageSuffix,
} from '@/components/Messenger/overlay/formatUnreadOverlayMessage';
import type { MessengerMessage, MessengerThread } from '@/api/client';
import { useMessengerNavbarStack } from '@/contexts/MessengerNavbarStackContext';
import './MessengerNewMessageOverlay.css';

const DISMISSED_IDS_STORAGE_PREFIX = 'messenger:newMsgOverlay:dismissedIds:';
const LEGACY_DISMISSED_SINGLE_PREFIX = 'messenger:newMsgOverlay:dismissedMsg:';

function dismissedIdsStorageKey(personId: string): string {
  return `${DISMISSED_IDS_STORAGE_PREFIX}${personId}`;
}

function readDismissedIds(personId: string | null): Set<string> {
  if (!personId || typeof sessionStorage === 'undefined') return new Set();
  const out = new Set<string>();
  try {
    const raw = sessionStorage.getItem(dismissedIdsStorageKey(personId));
    if (raw) {
      const parsed = JSON.parse(raw) as unknown;
      if (Array.isArray(parsed)) {
        for (const x of parsed) {
          if (typeof x === 'string' && x.trim()) out.add(x.trim());
        }
      }
    }
    const legacy = sessionStorage.getItem(`${LEGACY_DISMISSED_SINGLE_PREFIX}${personId}`)?.trim();
    if (legacy) {
      out.add(legacy);
      sessionStorage.removeItem(`${LEGACY_DISMISSED_SINGLE_PREFIX}${personId}`);
      sessionStorage.setItem(dismissedIdsStorageKey(personId), JSON.stringify([...out]));
    }
  } catch {
    /* ignore */
  }
  return out;
}

function writeDismissedIds(personId: string | null, ids: Set<string>): void {
  if (!personId || typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(dismissedIdsStorageKey(personId), JSON.stringify([...ids]));
  } catch {
    /* ignore */
  }
}

function mergeDismissedIds(personId: string | null, messageIds: string[]): void {
  if (!personId || messageIds.length === 0) return;
  const prev = readDismissedIds(personId);
  for (const id of messageIds) prev.add(id);
  writeDismissedIds(personId, prev);
}

function filterNotDismissed(items: UnreadOverlayItem[], dismissed: Set<string>): UnreadOverlayItem[] {
  return items.filter((i) => !dismissed.has(i.msg.id));
}

function threadFromSocket(
  msg: MessengerMessage,
  thread: MessengerSocketThreadMeta | null
): MessengerThread {
  const threadKind = thread?.thread_kind ?? 'direct';
  const isDirect = threadKind === 'direct';
  return {
    id: msg.thread_id,
    thread_kind: threadKind,
    title: thread?.title ?? null,
    last_message_at: msg.created_at,
    peer_id: isDirect ? thread?.peer_id ?? null : null,
    peer_name: isDirect ? thread?.peer_name ?? null : null,
    unread_count: 0,
  };
}

export interface MessengerNewMessageOverlayProps {
  personId: string | null;
  triggerRef: RefObject<HTMLElement | null>;
  popoverOpen: boolean;
  onOpenMessenger: () => void;
}

export function MessengerNewMessageOverlay({
  personId,
  triggerRef,
  popoverOpen,
  onOpenMessenger,
}: MessengerNewMessageOverlayProps) {
  const { setOverlayAnchorEl } = useMessengerNavbarStack();
  const [threadLoads, setThreadLoads] = useState<Record<string, UnreadOverlayThreadLoad> | null>(null);
  const lastShownRef = useRef<UnreadOverlayItem[] | null>(null);
  const itemsRef = useRef<UnreadOverlayItem[] | null>(null);
  const dismissedIdsRef = useRef<Set<string>>(new Set());
  const bootstrapInFlightRef = useRef(false);
  const popoverOpenRef = useRef(popoverOpen);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  popoverOpenRef.current = popoverOpen;

  const visibleItems = useMemo(() => {
    if (popoverOpen || !threadLoads || !personId) return [];
    const base = buildOverlayItemsFromLoads(threadLoads, personId);
    const dismissed = readDismissedIds(personId);
    return filterNotDismissed(base, dismissed).filter(
      (i) =>
        !shouldSuppressMessengerMessageNotification({
          threadId: i.msg.thread_id,
          senderPersonId: i.msg.sender_id,
          currentUserPersonId: personId,
        })
    );
  }, [threadLoads, personId, popoverOpen]);

  if (visibleItems.length > 0) lastShownRef.current = visibleItems;
  const displayed = visibleItems.length > 0 ? visibleItems : lastShownRef.current;
  itemsRef.current = displayed;

  const unreadCount = displayed?.length ?? 0;

  useEffect(() => {
    if (!personId) {
      dismissedIdsRef.current = new Set();
      setThreadLoads(null);
      return;
    }
    dismissedIdsRef.current = readDismissedIds(personId);
  }, [personId]);

  const open = Boolean(displayed && displayed.length > 0 && !popoverOpen);

  const handleExitComplete = useCallback(() => {
    const list = itemsRef.current;
    if (list?.length) {
      const ids = list.map((i) => i.msg.id);
      mergeDismissedIds(personId, ids);
      ids.forEach((id) => dismissedIdsRef.current.add(id));
    }
    setThreadLoads(null);
    lastShownRef.current = null;
  }, [personId]);

  const { shouldRender, contentClasses, requestClose } = useOverlayAnimation(
    open,
    handleExitComplete,
    true
  );

  usePopoverPositioning({
    triggerRef,
    popoverRef,
    isOpen: shouldRender,
    preferredPositionY: 'bottom',
    horizontalAlign: 'center',
    offset: 8,
    zIndex: 1001,
    placementRafDepth: 1,
    observeTriggerResize: false,
    repositionOnScroll: false,
    margin: { top: 12, bottom: 12, left: 12, right: 12 },
    dependencies: [unreadCount],
    positionDebugTag: 'messenger-new-message-overlay',
  });

  useLayoutEffect(() => {
    if (!shouldRender) {
      setOverlayAnchorEl(null);
      lastShownRef.current = null;
      return;
    }
    setOverlayAnchorEl(popoverRef.current);
    return () => {
      setOverlayAnchorEl(null);
    };
  }, [shouldRender, setOverlayAnchorEl, unreadCount]);

  useEffect(() => {
    if (!personId) return;

    const socket = getSocket();

    const onMessage = (raw: unknown) => {
      const { message: msg, thread } = unwrapMessengerMessageSocketPayload(raw);
      if (!msg?.thread_id || !msg.sender_id) return;

      if (popoverOpenRef.current) return;

      if (
        shouldSuppressMessengerMessageNotification({
          threadId: msg.thread_id,
          senderPersonId: msg.sender_id,
          currentUserPersonId: personId,
        })
      ) {
        return;
      }

      if (dismissedIdsRef.current.has(msg.id)) return;

      setThreadLoads((prev) => {
        const tid = msg.thread_id;
        const st = prev?.[tid];
        if (!st) {
          const block: UnreadOverlayThreadLoad = {
            thread: threadFromSocket(msg, thread),
            lastReadMessageId: null,
            loadedMessages: [msg],
            hasMore: false,
          };
          return prev ? { ...prev, [tid]: block } : { [tid]: block };
        }
        if (st.loadedMessages.some((m) => m.id === msg.id)) return prev;
        const merged = [...st.loadedMessages, msg].sort(
          (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
        return { ...prev!, [tid]: { ...st, loadedMessages: merged } };
      });
    };

    const runBootstrap = async () => {
      if (bootstrapInFlightRef.current) return;
      bootstrapInFlightRef.current = true;
      try {
        const loads = await fetchInitialUnreadThreadLoads();
        if (Object.keys(loads).length === 0) return;
        setThreadLoads(loads);
      } catch {
        /* ignore */
      } finally {
        bootstrapInFlightRef.current = false;
      }
    };

    const onMessagesBatch = (raw: unknown) => {
      const parsed = unwrapMessengerMessagesBatchSocketPayload(raw);
      if (!parsed) return;
      const { messages, thread } = parsed;
      const first = messages[0]!;
      if (!first?.thread_id || !first.sender_id) return;

      if (popoverOpenRef.current) return;

      if (
        shouldSuppressMessengerMessageNotification({
          threadId: first.thread_id,
          senderPersonId: first.sender_id,
          currentUserPersonId: personId,
        })
      ) {
        return;
      }

      const unseen = messages.filter((m) => !dismissedIdsRef.current.has(m.id));
      if (unseen.length === 0) return;

      setThreadLoads((prev) => {
        const tid = first.thread_id;
        const st = prev?.[tid];
        const ordered = [...unseen].sort(
          (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
        if (!st) {
          const head = ordered[0]!;
          const block: UnreadOverlayThreadLoad = {
            thread: threadFromSocket(head, thread),
            lastReadMessageId: null,
            loadedMessages: ordered,
            hasMore: false,
          };
          return prev ? { ...prev, [tid]: block } : { [tid]: block };
        }
        const merged = [...st.loadedMessages];
        for (const msg of ordered) {
          if (merged.some((m) => m.id === msg.id)) continue;
          merged.push(msg);
        }
        merged.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
        return { ...prev!, [tid]: { ...st, loadedMessages: merged } };
      });
    };

    socket.on('messenger:message', onMessage);
    socket.on('messenger:messages_batch', onMessagesBatch);
    const onConnect = () => void runBootstrap();
    socket.on('connect', onConnect);
    if (socket.connected) void runBootstrap();

    return () => {
      socket.off('messenger:message', onMessage);
      socket.off('messenger:messages_batch', onMessagesBatch);
      socket.off('connect', onConnect);
    };
  }, [personId]);

  const handleDismiss = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      requestClose();
    },
    [requestClose]
  );

  const handleOpenMessenger = useCallback(() => {
    onOpenMessenger();
    requestClose();
  }, [onOpenMessenger, requestClose]);

  const handleOpenMessengerKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      handleOpenMessenger();
    },
    [handleOpenMessenger]
  );

  if (!shouldRender || !displayed?.length) return null;

  const badgeLabel = unreadCount > 99 ? '99+' : String(unreadCount);
  const titleAriaLabel = formatUnreadOverlayMessage(unreadCount);
  const titleSuffix = formatUnreadOverlayMessageSuffix(unreadCount);

  const overlay = (
    <div
      ref={popoverRef}
      className="messenger-new-message-overlay"
      role="dialog"
      aria-labelledby="messenger-new-message-overlay-title"
      aria-label={`${titleAriaLabel}. Открыть мессенджер`}
      tabIndex={0}
      onClick={handleOpenMessenger}
      onKeyDown={handleOpenMessengerKeyDown}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className={`messenger-new-message-overlay__popup ${contentClasses}`.trim()}>
        <div className="messenger-new-message-overlay__header">
          <span
            className="messenger-new-message-overlay__title"
            id="messenger-new-message-overlay-title"
          >
            У вас{' '}
            <span className="messengerUnreadBadge messenger-new-message-overlay__countBadge" aria-hidden>
              {badgeLabel}
            </span>{' '}
            {titleSuffix}
          </span>
          <button
            type="button"
            className="messenger-new-message-overlay__close"
            onClick={handleDismiss}
            aria-label="Закрыть"
            title="Закрыть"
          >
            <CloseIcon size={20} />
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
}
